import { useEffect, useRef, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { BlockApproval, BlockLevel } from "../../api/types";
import { UserRefChip } from "../../components/UserRefChip";
import { formatDateTime } from "../../lib/format";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { Link } from "../../router/Router";
import { levelLabel } from "./BlockCard";
import { dueDateLabel } from "./dueDate";

interface State {
  status: "loading" | "ready" | "error";
  approvals: BlockApproval[];
  errorMessage: string | null;
}

const initial: State = { status: "loading", approvals: [], errorMessage: null };

// The proposed child a sub_block request carries (the server's subBlockPayload,
// C.5). A request queued before due dates existed has no due_date key at all.
interface SubBlockProposal {
  parent_id: string;
  level: BlockLevel;
  name: string;
  size: number;
  due_date?: string;
  team?: string;
}

// ApprovalsPage is the sign-off queue (C.4, C.5): pending completions and
// Execution sub-block requests. Approving a completion completes the block;
// rejecting sends it back to in-progress. Approving a request creates the
// sub-block; rejecting creates nothing.
export function ApprovalsPage() {
  const [state, setState] = useState<State>(initial);
  // Every decision starts a refresh, so they overlap; only the latest may land,
  // or an older response would bring back a row that was just decided.
  const latestRefresh = useRef(0);

  useEffect(() => {
    void refresh();
  }, []);

  // A refresh keeps the current list on screen rather than swapping it for
  // "Loading…", so a reason being typed in another row survives it.
  async function refresh() {
    const request = ++latestRefresh.current;
    try {
      const approvals = await fetchPendingApprovals();
      if (request === latestRefresh.current) {
        setState({ status: "ready", approvals, errorMessage: null });
      }
    } catch (caught) {
      if (request === latestRefresh.current) {
        setState({ status: "error", approvals: [], errorMessage: messageFor(caught) });
      }
    }
  }

  function decided(id: string) {
    setState((previous) => ({ ...previous, approvals: previous.approvals.filter((approval) => approval.id !== id) }));
    void refresh();
  }

  return (
    <div className="blocks-view">
      <section className="card">
        <h2>Approvals</h2>
        {state.status === "loading" && <p className="loading">Loading…</p>}
        {state.status === "error" && state.errorMessage !== null && (
          <p className="form-row__error">{state.errorMessage}</p>
        )}
        {state.status === "ready" && state.approvals.length === 0 && (
          <p className="muted">Nothing is waiting for approval.</p>
        )}
        {state.status === "ready" && state.approvals.length > 0 && (
          <div className="subblock-tree">
            {state.approvals.map((approval) => (
              <ApprovalRow key={approval.id} approval={approval} onDecided={() => decided(approval.id)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ApprovalRow({ approval, onDecided }: { approval: BlockApproval; onDecided: () => void }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keyFor, reset } = useIdempotencyKey();
  // A closed season's record is frozen, so its requests can only be rejected.
  const seasonClosed = approval.season_status === "closed";

  async function decide(approve: boolean) {
    const trimmedReason = reason.trim();
    if (submitting || (!approve && trimmedReason === "")) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (approve) {
        await apiClient.approveBlockApproval(approval.id, keyFor(`approve:${approval.id}`));
      } else {
        await apiClient.rejectBlockApproval(approval.id, trimmedReason, keyFor(`reject:${approval.id}:${trimmedReason}`));
      }
      reset();
      onDecided();
    } catch (caught) {
      // The server replays a stored answer for a reused key, so a refused
      // decision needs a new key to be retried; a network error may never have
      // reached the server, so that retry keeps the key.
      if (caught instanceof ApiError) {
        reset();
      }
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  let title: React.ReactNode;
  let details: React.ReactNode;
  let kindLabel: string;
  if (approval.kind === "sub_block") {
    const proposal = approval.payload as unknown as SubBlockProposal;
    kindLabel = `New ${levelLabel(proposal.level).toLowerCase()}`;
    title = proposal.name;
    details = (
      <>
        {levelLabel(proposal.level)} under{" "}
        <Link to={`/blocks/${proposal.parent_id}`}>{approval.block_name ?? "its parent"}</Link>
        {` · Size ${proposal.size} · ${dueDateLabel(proposal.due_date ? proposal.due_date : null)}`}
        {proposal.team !== undefined ? ` · ${proposal.team}` : ""}
      </>
    );
  } else {
    kindLabel = "Completion";
    title = <Link to={`/blocks/${approval.block_id}`}>{approval.block_name ?? "Open block"}</Link>;
    details = "Marked complete — approving completes it, rejecting sends it back to in progress.";
  }

  return (
    <div className="approval-row">
      <div className="approval-row__main">
        <div className="block-card__tags">
          <span className="chip">{kindLabel}</span>
          {approval.requested_by != null && <UserRefChip user={approval.requested_by} title="Requested by" />}
          <span className="chip">{formatDateTime(approval.created_at)}</span>
          {seasonClosed && <span className="chip">Season closed</span>}
        </div>
        <div className="subblock-row__name">{title}</div>
        <span className="subblock-row__meta">{details}</span>
      </div>
      <div className="row-actions">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={submitting || seasonClosed}
          onClick={() => void decide(true)}
        >
          Approve
        </button>
        <button
          type="button"
          className="btn btn--danger btn--sm"
          disabled={submitting}
          onClick={() => setRejecting((open) => !open)}
        >
          {rejecting ? "Cancel" : "Reject…"}
        </button>
      </div>
      {rejecting && (
        <form
          className="form-row form-row--inline approval-row__reject"
          onSubmit={(event) => {
            event.preventDefault();
            void decide(false);
          }}
        >
          <label>
            Reason
            <input
              type="text"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Why it's being sent back"
              required
            />
          </label>
          <button type="submit" className="btn btn--danger" disabled={submitting || reason.trim() === ""}>
            {submitting ? "Rejecting…" : "Reject"}
          </button>
        </form>
      )}
      {error !== null && <p className="form-row__error approval-row__error">{error}</p>}
    </div>
  );
}

// fetchPendingApprovals follows the keyset cursor so a queue longer than one
// page (max 100) is shown whole.
async function fetchPendingApprovals(): Promise<BlockApproval[]> {
  const approvals: BlockApproval[] = [];
  let cursor: string | undefined;
  do {
    const page = await apiClient.listBlockApprovals({ status: "pending", limit: 100, cursor });
    approvals.push(...page.approvals);
    cursor = page.next_cursor;
  } while (cursor !== undefined);
  return approvals;
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Operation failed.";
}
